<template>
    <div>
        <v-container fluid pa-0>
            <v-container fluid grid-list-xl class="toolbar-container">
                <v-layout row wrap :class="$bp.smallScreen ? 'mx-0' : null">
                    <v-toolbar color="transparent" flat elevation="0">
                        <v-toolbar-title class="ml-0">{{ $t($route.meta.title) }}<bc-breadcrumbs :items="breadcrumb_items"></bc-breadcrumbs></v-toolbar-title>
                        <v-spacer></v-spacer>
                        <bc-modal-print
                            ref="print"
                            @close-method="(file_name = null), $refs.print.close()"
                            :file-name="file_name"
                            :no-button="true"
                        ></bc-modal-print>
                        <bc-btn-refresh
                            v-if="subscription.subscription_id && subscription.subscription_id > 0"
                            @invoke-method="dbGetSubscription(true)"
                        ></bc-btn-refresh>
                        <v-menu left content-class="no-stripe" v-if="subscription.subscription_id && subscription.subscription_id > 0">
                            <template slot="activator">
                                <v-btn icon>
                                    <v-icon>more_vert</v-icon>
                                </v-btn>
                            </template>
                            <v-list>
                                <v-list-tile @click="printPDF()">
                                    <v-list-tile-title> {{ $t("lang.print") }} </v-list-tile-title>
                                </v-list-tile>
                                <template v-if="hasPerm('subscriptions.update')">
                                    <v-divider class="ml-3 my-0"></v-divider>

                                    <div v-for="item in subscription_status_options" :key="item.option_id">
                                        <v-list-tile @click="changeStatus(item)" v-if="item.option_id != subscription.status_id">
                                            <v-list-tile-title>
                                                {{ $t(`lang.mark_as_${item.slug}`) ? $t(`lang.mark_as_${item.slug}`) : `Mark as ${item.option}` }}
                                            </v-list-tile-title>
                                        </v-list-tile>
                                    </div>
                                </template>
                                <template v-if="hasPerm('subscriptions.delete')">
                                    <v-divider class="ml-3 my-0"></v-divider>
                                    <v-list-tile @click="$refs.delete_subscription.openDialog()">
                                        <v-list-tile-title> {{ $t("lang.delete") }} </v-list-tile-title>
                                    </v-list-tile>
                                </template>
                            </v-list>
                        </v-menu>
                        <bc-tooltip>
                            <template slot="activator">
                                <v-btn icon @mouseup.left="$router.push({ name: 'subscriptions' })">
                                    <v-icon>close</v-icon>
                                </v-btn>
                            </template>
                            {{ $t("lang.back_to_list") }}
                        </bc-tooltip>
                    </v-toolbar>
                </v-layout>
            </v-container>

            <v-card-text>
                <v-card elevation="10">
                    <v-card-text>
                        <div v-if="subscription.status" class="ribbon ribbon-top-right" :class="`ribbon-${subscription.status_class}`">
                            <span class="ribbon-span">{{ subscription.status }}</span>
                        </div>
                        <v-form ref="formSubscription">
                            <v-flex xs12>
                                <v-text-field
                                    v-focus="subscription_dialog"
                                    v-model="subscription.subscription_id"
                                    :label="$t('lang.subscription_id')"
                                    :counter="showCounter(subscription.subscription_id, 'subscriptions.subscription_id')"
                                    :rules="ifValid('subscriptions.subscription_id')"
                                ></v-text-field>
                            </v-flex>
                            <v-flex xs12>
                                <bc-autocomplete
                                    v-model="subscription.device_id"
                                    :label="$t('lang.device_id')"
                                    :items="iot_devices"
                                    item-value="device_id"
                                    item-text="device_id"
                                    :rules="ifValid('subscriptions.device_id')"
                                ></bc-autocomplete>
                            </v-flex>
                            <v-flex xs12>
                                <bc-autocomplete
                                    v-model="subscription.account_id"
                                    :label="$t('lang.account_id')"
                                    :items="customers"
                                    item-value="account_id"
                                    item-text="company"
                                    :rules="ifValid('subscriptions.account_id')"
                                ></bc-autocomplete>
                            </v-flex>
                            <v-flex xs12>
                                <v-text-field
                                    v-model="subscription.stripe_subscription_id"
                                    :label="$t('lang.stripe_subscription_id')"
                                    :counter="showCounter(subscription.stripe_subscription_id, 'subscriptions.stripe_subscription_id')"
                                    :rules="ifValid('subscriptions.stripe_subscription_id')"
                                ></v-text-field>
                            </v-flex>
                            <v-flex xs12>
                                <v-text-field
                                    v-model="subscription.plan_name"
                                    :label="`${$t('lang.plan_name')} *`"
                                    :counter="showCounter(subscription.plan_name, 'subscriptions.plan_name')"
                                    :rules="ifValid('subscriptions.plan_name')"
                                ></v-text-field>
                            </v-flex>
                            <v-flex xs12>
                                <v-text-field
                                    v-model="subscription.amount"
                                    :label="`${$t('lang.amount')} *`"
                                    :counter="showCounter(subscription.amount, 'subscriptions.amount')"
                                    :rules="ifValid('subscriptions.amount')"
                                ></v-text-field>
                            </v-flex>
                            <v-flex xs12>
                                <v-text-field
                                    v-model="subscription.device_seats"
                                    :label="`${$t('lang.device_seats')} *`"
                                    :counter="showCounter(subscription.device_seats, 'subscriptions.device_seats')"
                                    :rules="ifValid('subscriptions.device_seats')"
                                ></v-text-field>
                            </v-flex>
                            <v-flex xs12>
                                <bc-date-picker
                                    v-model="subscription.period_start"
                                    :label="`${$t('lang.period_start')} *`"
                                    :rules="ifValid('subscriptions.period_start')"
                                ></bc-date-picker>
                            </v-flex>
                            <v-flex xs12>
                                <v-text-field
                                    v-model="subscription.term_length"
                                    :label="$t('lang.term_length')"
                                    :counter="showCounter(subscription.term_length, 'subscriptions.term_length')"
                                    :rules="ifValid('subscriptions.term_length')"
                                ></v-text-field>
                            </v-flex>
                            <v-flex xs12>
                                <bc-date-picker
                                    v-model="subscription.period_end"
                                    :label="`${$t('lang.period_end')} *`"
                                    :rules="ifValid('subscriptions.period_end')"
                                ></bc-date-picker>
                            </v-flex>
                            <v-flex xs12>
                                <bc-select
                                    v-model="subscription.paid"
                                    :label="`${$t('lang.paid')} *`"
                                    :items="[]"
                                    item-value="value"
                                    item-text="text"
                                    :rules="ifValid('subscriptions.paid')"
                                ></bc-select>
                            </v-flex>
                            <v-flex xs12>
                                <v-text-field
                                    v-model="subscription.status"
                                    :label="`${$t('lang.status')} *`"
                                    :counter="showCounter(subscription.status, 'subscriptions.status')"
                                    :rules="ifValid('subscriptions.status')"
                                ></v-text-field>
                            </v-flex>
                            <v-flex xs12>
                                <v-text-field
                                    v-model="subscription.invoice_id"
                                    :label="`${$t('lang.invoice_id')} *`"
                                    :counter="showCounter(subscription.invoice_id, 'subscriptions.invoice_id')"
                                    :rules="ifValid('subscriptions.invoice_id')"
                                ></v-text-field>
                            </v-flex>
                            <v-flex xs12>
                                <v-textarea
                                    v-model="subscription.notes"
                                    :label="$t('lang.notes')"
                                    auto-grow
                                    rows="1"
                                    :rules="ifValid('subscriptions.notes')"
                                ></v-textarea>
                            </v-flex>
                        </v-form>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn @mouseup.left="$router.push({ name: 'subscriptions' })" flat>{{ $t("lang.cancel") }}</v-btn>
                        <v-btn color="success" @click="saveSubscriptions">{{ $t("lang.save") }}</v-btn>
                    </v-card-actions>
                </v-card>
            </v-card-text>

            <delete-confirmation-dialog
                ref="delete_subscription"
                :headline="$t('lang.confirm_delete')"
                :heading="$t('lang.delete_subscription')"
                :message="$t('lang.delete_subscription_confirm')"
                @onConfirm="deleteSubscriptions()"
            ></delete-confirmation-dialog>
            <delete-confirmation-dialog
                ref="delete_subscription_details"
                :heading="$t('lang.delete_package')"
                @onConfirm="confirmDeleteSubscriptionDetails()"
            ></delete-confirmation-dialog>
        </v-container>
    </div>
</template>
<script>
import { mapState, mapActions, mapMutations } from "vuex";
import { store } from "Store/store";

export default {
    data() {
        return {
            breadcrumb_items: [
                {
                    text: "Dashboard",
                    link: true,
                    exact: true,
                    disabled: false,
                    to: {
                        name: "dashboard",
                    },
                },
                {
                    text: "Subscriptions",
                    link: true,
                    exact: true,
                    disabled: false,
                    to: {
                        name: "subscriptions",
                    },
                },
                {
                    text: "Subscription",
                    disabled: true,
                    href: "",
                },
            ],
            mobile_list: [{ title: this.$t("lang.print"), action: "printPDF" }],

            formData: {},
            subscription: {},
            file_name: "",
            dbGetSubscription: _.debounce(this.getSubscription, 1500, { maxWait: 1500, leading: true, trailing: false }),
            customers: null,
        };
    },
    beforeRouteUpdate(to, from, next) {
        const subscription_id = `${to.params.subscription_id}`;

        if (subscription_id !== "undefined") {
            this.$store
                .dispatch("fetchSubscription", {
                    subscription_id: subscription_id,
                })
                .then((data) => {
                    if (typeof data !== "undefined" && data.subscription) {
                        this.subscription = this.assignObj2(data.subscription, this.rules_subscriptions);

                        this.$store.dispatch("fetchSetting", {
                            url: "options?slug=subscription_status&sort_by=2",
                            method: "get",
                            stateProperty: "subscription_status_options",
                            dataProperty: "options",
                            commit: true,
                        });

                        this.finishLoader();
                        next();
                    } else {
                        next({ name: "subscriptions", replace: true });
                    }
                });
        } else {
            this.finishLoader();
            next();
        }
    },
    beforeRouteEnter(to, from, next) {
        const subscription_id = `${to.params.subscription_id}`;

        if (subscription_id !== "undefined") {
            store
                .dispatch("fetchSubscription", {
                    subscription_id: subscription_id,
                })
                .then((data) => {
                    if (typeof data !== "undefined" && data.subscription) {
                        next((vm) => {
                            vm.subscription = vm.assignObj2(data.subscription, vm.rules_subscriptions);

                            store.dispatch("fetchSetting", {
                                url: "options?slug=subscription_status&sort_by=2",
                                method: "get",
                                stateProperty: "subscription_status_options",
                                dataProperty: "options",
                                commit: true,
                            });

                            vm.finishLoader();
                        });
                    } else {
                        next({ name: "subscriptions", replace: true });
                    }
                });
        } else {
            next((vm) => {
                vm.finishLoader();
            });
        }

        /*store.dispatch("fetchSetting", {
            url: "options?slug=subscription_status&sort_by=2",
            method: "get",
            stateProperty: "subscription_status_options",
            dataProperty: "options",
            commit: true,
        });*/
    },
    computed: {
        ...mapState({
            rules_subscriptions: (state) => state.subscriptions.rules_subscriptions,
            subscription_status_options: (state) => state.settings.subscription_status_options,
            subscriptions: (state) => state.subscriptions.subscriptions,
            selectedSubscription: (state) => state.subscriptions.selectedSubscription,
            iot_devices: (state) => state.iot_devices.iot_devices,
        }),
    },
    methods: {
        ...mapActions(["fetchSubscription", "saveSubscription", "deleteSubscription", "printSubscription", "fetchIotDevices", "fetchCustomers"]),
        ...mapMutations(["validationOn", "validationOff", "toggleGlobalSnackbar", "toggleModalLoader", "clearSubscriptions"]),

        getSubscription(pg_refresh = false) {
            this.startLoader();

            this.fetchSubscription({
                subscription_id: `${this.$route.params.subscription_id}`,
            }).then((data) => {
                if (typeof data !== "undefined" && data.subscription) {
                    this.subscription = this.assignObj2(data.subscription, this.rules_subscriptions);
                    this.toggleModalLoader(false);
                    this.validationOff();
                    this.finishLoader(pg_refresh);
                }
            });
        },
        printPDF() {
            this.dev_mode ? this.$refs.print.openDialog() : this.toggleGlobalSnackbar(true);

            this.printSubscription({
                subscription_id: `${this.$route.params.subscription_id}`,
            }).then((data) => {
                if (typeof data != "undefined" && data.subscription) {
                    if (this.dev_mode) {
                        this.file_name = data.subscription.file_name;
                    } else {
                        this.$refs.print.printReport(data.subscription.file_name);
                    }

                    if (!this.dev_mode) this.toggleGlobalSnackbar(false);
                } else {
                    this.file_name = "";
                    this.$refs.print.close();
                    this.createAlert();
                }
            });
        },
        saveSubscriptions() {
            this.validationOn();
            this.$nextTick(() => {
                if (this.validateForm("formSubscription")) {
                    let body = this.prepForm(this.subscription, this.rules_subscriptions);

                    this.saveSubscription({
                        body: body,
                    }).then((data) => {
                        if (typeof data != "undefined" && data.subscription) {
                            this.$router.push({ name: "subscription", params: { subscription_id: data.subscription.subscription_id } });
                        } else {
                        }
                    });
                }
            });
        },
        changeStatus(item) {
            this.status_loader = true;
            let body = { subscription_id: `${this.$route.params.subscription_id}`, status_id: item.option_id };

            this.saveSubscriptions({
                body: body,
            }).then((data) => {
                if (typeof data != "undefined" && data.subscription) {
                    this.subscription = this.assignObj2(data.subscription, this.rules_subscriptions);
                }
            });
        },
        deleteSubscriptions() {
            this.deleteSubscription({
                subscription_id: `${this.$route.params.subscription_id}`,
            }).then((data) => {
                console.log(data);
                if (data && data.code == "200") {
                    this.validationOff();
                    this.$router.push({ name: "subscriptions" });
                }
                this.toggleModalLoader(false);
            });
        },
        async refresh(force = false) {
            this.startLoader();

            if (force == true) {
                await this.fetchSubscription({
                    subscription_id: `${this.$route.params.subscription_id}`,
                }).then((data) => {
                    if (typeof data !== "undefined" && data.subscription) {
                        this.subscription = this.assignObj2(data.subscription, this.rules_subscriptions);
                        this.toggleModalLoader(false);
                        this.validationOff();
                        this.finishLoader(true);
                    }
                });
            } else {
                this.finishLoader();
            }

            if (this.iot_devices.length == 0 || force == true) await this.fetchIotDevices({ body: { options: 1 } });
            if (this.customers === null || force == true) await this.getCustomers();
        },
    },
    mounted() {
        this.refresh();
    },
    created() {},
    beforeRouteLeave(to, from, next) {
        this.$store.commit("clearSubscriptions");
        next();
    },
};
</script>
